import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const emptyForm = {
  title: '',
  industry: '',
  founded_year: 2025,
  stammkapital: 25000,
  price: 0,
  location: '',
  status: 'Verfügbar',
  description: '',
  featuresText: '',
  published: true,
};

const AdminListings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;

  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [listings, setListings] = useState([]);

  const [editing, setEditing] = useState(null); // listing object or null
  const [form, setForm] = useState(emptyForm);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const load = async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings`, { headers });
      setListings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Fehler', description: 'Bitte logge dich erneut ein.' });
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Intentionally run once on mount; subsequent reloads happen after create/update/delete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return listings;
    const q = query.toLowerCase();
    return listings.filter((l) => JSON.stringify(l).toLowerCase().includes(q));
  }, [listings, query]);

  const startCreate = () => {
    setEditing({ id: null });
    setForm(emptyForm);
  };

  const startEdit = (l) => {
    setEditing(l);
    setForm({
      title: l.title,
      industry: l.industry,
      founded_year: l.founded_year,
      stammkapital: l.stammkapital,
      price: l.price,
      location: l.location,
      status: l.status,
      description: l.description,
      featuresText: (l.features || []).join('\n'),
      published: !!l.published,
    });
  };

  const save = async () => {
    try {
      const payload = {
        title: form.title,
        industry: form.industry,
        founded_year: Number(form.founded_year),
        stammkapital: Number(form.stammkapital),
        price: Number(form.price),
        location: form.location,
        status: form.status,
        description: form.description,
        features: form.featuresText
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        published: !!form.published,
      };

      if (!editing?.id) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings`, payload, { headers });
        toast({ title: 'Listing erstellt' });
      } else {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${editing.id}`, payload, { headers });
        toast({ title: 'Listing gespeichert' });
      }

      setEditing(null);
      await load();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Speichern fehlgeschlagen',
        description: err.response?.data?.detail || 'Bitte versuche es erneut.',
      });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Listing wirklich löschen?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${id}`, { headers });
      toast({ title: 'Listing gelöscht' });
      await load();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Löschen fehlgeschlagen' });
    }
  };

  return (
    <div className="min-h-screen pt-40 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Listings verwalten</h1>
            <p className="text-sm text-gray-600">Marktplatz-Angebote erstellen, bearbeiten, veröffentlichen.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="outline">Zurück</Button>
            </Link>
            <Button variant="outline" onClick={logout}>Logout</Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 items-center justify-between mb-4">
          <div className="w-full md:w-96">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Suchen…" />
          </div>
          <Button className="bg-gray-900 hover:bg-gray-800 text-white" onClick={startCreate}>
            Neues Listing
          </Button>
        </div>

        {editing ? (
          <Card className="border-2 border-gray-200 shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="text-xl">{editing.id ? 'Listing bearbeiten' : 'Neues Listing'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Branche</label>
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gegründet (Jahr)</label>
                  <Input type="number" value={form.founded_year} onChange={(e) => setForm({ ...form, founded_year: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stammkapital (€)</label>
                  <Input type="number" value={form.stammkapital} onChange={(e) => setForm({ ...form, stammkapital: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kaufpreis (€)</label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Standort</label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status (z.B. Verfügbar/Reserviert)</label>
                  <Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="published"
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  />
                  <label htmlFor="published" className="text-sm text-gray-700">Veröffentlicht</label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Besonderheiten (1 pro Zeile)</label>
                  <textarea
                    value={form.featuresText}
                    onChange={(e) => setForm({ ...form, featuresText: e.target.value })}
                    rows={5}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <Button className="bg-gray-900 hover:bg-gray-800 text-white" onClick={save}>
                  Speichern
                </Button>
                <Button variant="outline" onClick={() => setEditing(null)}>
                  Abbrechen
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border-2 border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Alle Listings ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-600">Lade…</div>
            ) : (
              <div className="overflow-auto border border-gray-200 rounded-lg bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left font-semibold px-3 py-2">Titel</th>
                      <th className="text-left font-semibold px-3 py-2">Branche</th>
                      <th className="text-left font-semibold px-3 py-2">Standort</th>
                      <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">Preis</th>
                      <th className="text-left font-semibold px-3 py-2">Status</th>
                      <th className="text-left font-semibold px-3 py-2">Veröffentlicht</th>
                      <th className="text-right font-semibold px-3 py-2">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr key={l.id} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900">{l.title}</td>
                        <td className="px-3 py-2 text-gray-700">{l.industry}</td>
                        <td className="px-3 py-2 text-gray-700">{l.location}</td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{Number(l.price).toLocaleString('de-DE')} €</td>
                        <td className="px-3 py-2 text-gray-700">{l.status}</td>
                        <td className="px-3 py-2 text-gray-700">{l.published ? 'Ja' : 'Nein'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button variant="outline" size="sm" onClick={() => startEdit(l)} className="mr-2">
                            Bearbeiten
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => remove(l.id)}>
                            Löschen
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">Keine Listings.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminListings;
